export const formatDateTime = (iso: string): string =>
  new Date(iso).toLocaleString();

export const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString();

export const formatTime = (iso: string): string =>
  new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

const RELATIVE = new Intl.RelativeTimeFormat([], { numeric: "auto" });

const RELATIVE_STEPS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["second", 60],
  ["minute", 60],
  ["hour", 24],
  ["day", 7],
  ["week", 4.35],
  ["month", 12],
];

/** "8 minutes ago" / "in 2 hours" — walks up the units until the count fits. */
export const formatRelativeTime = (iso: string): string => {
  let delta = (new Date(iso).getTime() - Date.now()) / 1000;
  for (const [unit, span] of RELATIVE_STEPS) {
    if (Math.abs(delta) < span) return RELATIVE.format(Math.round(delta), unit);
    delta /= span;
  }
  return RELATIVE.format(Math.round(delta), "year");
};

/** "Jun 23" — compact axis/label form. */
export const formatMonthDay = (iso: string): string =>
  new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" });

/** "9:04 AM" today, "Jul 22, 9:04 AM" this year, "Jul 22, 2025, 9:04 AM" before —
 * `formatDateTime`'s seconds don't fit a narrow column, but the year has to appear
 * once it leaves the current one, since a list of these can span years. */
export const formatPastDateTime = (iso: string): string => {
  const at = new Date(iso);
  const now = new Date();
  if (at.toDateString() === now.toDateString()) return formatTime(iso);
  const day =
    at.getFullYear() === now.getFullYear()
      ? formatMonthDay(iso)
      : at.toLocaleDateString([], {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
  return `${day}, ${formatTime(iso)}`;
};

/** "7/22/2026, 9:04 AM–2:30 PM" same-day; "Apr 3, 9:04 AM – Jul 22, 2:30 PM"
 * otherwise — the range always carries its date(s), and a range spanning days
 * must not read as a same-day range. */
export const formatTimeRange = (startIso: string, endIso: string): string => {
  const sameDay =
    new Date(startIso).toDateString() === new Date(endIso).toDateString();
  if (sameDay)
    return `${formatDate(startIso)}, ${formatTime(startIso)}–${formatTime(endIso)}`;
  return `${formatDate(startIso)}, ${formatTime(startIso)} – ${formatDate(endIso)}, ${formatTime(endIso)}`;
};
